import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import type { Row, RowData } from "@tanstack/react-table-v9";

import GridBody from "./GridBody";
import GridFooter, { hasFooterRow } from "./GridFooter";
import GridHeader from "./GridHeader";
import { fitColumnToContent } from "../../columnUtils";
import type { TableV2Features } from "../../features";
import {
  AUTO_COLUMN_LIMIT,
  AUTO_ROW_LIMIT,
  useColumnVirtualizer,
  useRowVirtualizer,
} from "../../hooks/useGridVirtualizer";
import type { TableV2Instance } from "../../hooks/useTableV2";
import { Grid, ScrollArea } from "../../styles";
import ColumnPicker from "../ColumnPicker";

export interface TableV2GridProps<TData extends RowData> {
  table: TableV2Instance<TData>;
  isLoading?: boolean;
  /** Shown instead of rows, with a Retry button when `onRetry` is set */
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
  /** Full-width panel under an expanded row */
  renderSubComponent?: (row: Row<TableV2Features, TData>) => React.ReactNode;
  /** Bold rows such as pivot groups */
  rowEmphasis?: (row: Row<TableV2Features, TData>) => boolean;
  hideHeader?: boolean;
  /** Scroll the body vertically past this height, header stays visible */
  maxBodyHeight?: number;
  /** Keep the body the same height while paging, sorting, filtering and loading */
  stableHeight?: boolean;
  /** Skeleton rows shown while loading. Defaults to the page size, or 5 without pagination */
  skeletonRows?: number;
  /** Per-column menu with sort, pin, reset width and hide */
  columnMenu?: boolean;
  /** Drag headers to reorder columns */
  columnDrag?: boolean;
  /** Columns icon over the header's right end for show/hide and reorder */
  columnPicker?: boolean;
  /** Icon buttons over the header's right end, before the column picker. Take no column width */
  headerActions?: React.ReactNode[];
  /** Footer values by column id, e.g. totals from the API in server mode. Overrides `meta.total` */
  totals?: Record<string, unknown>;
  /** Shown in the first footer cell that has no value */
  footerLabel?: string;
  /** Height of a body row in px, border included. Also sizes the body to the page size so paging doesn't jump */
  rowHeight?: number;
  /** Render only the rows and columns in view. "auto" turns it on past 100 rows or 30 unpinned columns */
  virtualize?: boolean | "auto";
}

export const DEFAULT_ROW_HEIGHT = 45;

/** Header, body and the loading, empty and error states. Pagination and toolbar are separate pieces. */
const TableV2Grid = <TData extends RowData>({
  table,
  isLoading = false,
  error,
  onRetry,
  emptyMessage = "No data available",
  renderSubComponent,
  rowEmphasis,
  hideHeader = false,
  maxBodyHeight,
  stableHeight = true,
  skeletonRows,
  columnMenu = true,
  columnDrag = true,
  columnPicker = false,
  headerActions,
  totals,
  footerLabel = "Total",
  virtualize = "auto",
  rowHeight = DEFAULT_ROW_HEIGHT,
}: TableV2GridProps<TData>) => {
  const icons = [
    ...(headerActions ?? []),
    columnPicker && <ColumnPicker key="columns" table={table} />,
  ].filter(Boolean);
  const bodyRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;
  const { pageSize } = table.state.pagination;
  const paginated =
    !table.options.manualPagination || table.options.rowCount !== undefined;
  const centerColumns = table.getCenterVisibleLeafColumns();

  const virtualRows =
    virtualize === true ||
    (virtualize === "auto" && rows.length > AUTO_ROW_LIMIT);
  const rowWindow = useRowVirtualizer({
    enabled: virtualRows && !isLoading && !error,
    count: rows.length,
    scrollRef,
    bodyRef,
    ownScroll: !!maxBodyHeight,
    rowHeight,
  });
  const columnWindow = useColumnVirtualizer({
    enabled:
      virtualize === true ||
      (virtualize === "auto" && centerColumns.length > AUTO_COLUMN_LIMIT),
    sizes: centerColumns.map((column) => column.getSize()),
    startWidth: table.getStartTotalSize(),
    scrollRef,
  });
  // A virtual body is as tall as all its rows already
  const lockHeight = stableHeight && paginated && !virtualRows;

  // Short last pages, loading and empty results keep the height of a full page. Data that fits on one page sizes to it
  const rowCount = table.getRowCount();
  const reservedRows =
    isLoading || rowCount === 0 ? pageSize : Math.min(pageSize, rowCount);
  // Header, footer and a horizontal scrollbar take part of the scroll area
  const [chromeHeight, setChromeHeight] = useState(0);
  useLayoutEffect(() => {
    const grid = gridRef.current;
    const body = bodyRef.current;
    const scroller = scrollRef.current;
    if (!grid || !body || !scroller) return;
    const next =
      grid.offsetHeight -
      body.offsetHeight +
      scroller.offsetHeight -
      scroller.clientHeight;
    if (next !== chromeHeight) setChromeHeight(next);
  });
  // Never taller than the visible area, so empty space can't scroll
  const lockedHeight = lockHeight
    ? Math.min(
        reservedRows * rowHeight,
        maxBodyHeight ? maxBodyHeight - chromeHeight : Infinity,
      )
    : 0;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [table.state.pagination.pageIndex, pageSize]);

  return (
    <ScrollArea ref={scrollRef} $maxBodyHeight={maxBodyHeight}>
      <Grid
        ref={gridRef}
        role="table"
        style={{ minWidth: table.getTotalSize() }}
      >
        {!hideHeader && (
          <GridHeader
            table={table}
            icons={icons}
            columnMenu={columnMenu}
            columnDrag={columnDrag}
            columnWindow={columnWindow}
            onFit={(columnId) =>
              fitColumnToContent(table, gridRef.current, columnId)
            }
          />
        )}
        <div
          ref={bodyRef}
          role="rowgroup"
          style={{
            minHeight: lockedHeight || undefined,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <GridBody
            table={table}
            isLoading={isLoading}
            error={error}
            onRetry={onRetry}
            emptyMessage={emptyMessage}
            skeletonCount={skeletonRows ?? (paginated ? pageSize : 5)}
            rowHeight={rowHeight}
            renderSubComponent={renderSubComponent}
            rowEmphasis={rowEmphasis}
            virtualRows={virtualRows}
            rowWindow={rowWindow}
            columnWindow={columnWindow}
          />
        </div>
        {hasFooterRow(table) && !error && (
          <GridFooter
            table={table}
            totals={totals}
            footerLabel={footerLabel}
            columnWindow={columnWindow}
          />
        )}
      </Grid>
    </ScrollArea>
  );
};

export default TableV2Grid;
